import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { NotificationDataInstanceToken, NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { EventService } from '../../services/event/event.service';
import { TimeRecordingService } from '../../services/time-recording/time-recording.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { ArrayModel } from '../complex/array.model';
import { CodeModel } from '../complex/code.model';
import { CompositeModel } from '../complex/composite.model';
import { CrmCodeModel } from '../complex/crm-code.model';
import { DropdownModel } from '../complex/dropdown.model';
import { NotificationModel } from '../low-level/notification.model';
import { StatusModel } from '../low-level/status.model';
import { TimesheetModel } from '../low-level/timesheet.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import {
    TIMERECORDING_MEANS_OF_TRANSPORT_CODES,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from './time-recording-entry.model';
import {
    isTimeRecordingGeneralEntryModel,
    JsonTimeRecordingGeneralEntryModel,
    TimeRecordingGeneralEntryModel,
    TimeRecordingGeneralEntryModelFromJson,
} from './time-recording-general-entry.model';
import {
    JsonTimeRecordingLimitedEntryModel,
    TimeRecordingLimitedEntryModel,
    TimeRecordingLimitedEntryModelFromJson,
} from './time-recording-limited-entry.model';
import { TimesheetEntryInterface } from './timesheet-entry.interface';
import { TimesheetEntryModel } from './timesheet-entry.model';
import { BooleanModel } from '../primitive/boolean.model';

/** Name of the type-of-work dropdown of a time recording entry, and therefore of its field in the payload */
const WORK_CODE_FIELD = 'WorkCode';

/** Work codes that exist on the device only, mapped to the code the backend expects instead */
const NON_BILLABLE_REMOTE_WORK_CODE_SUBSTITUTES: Partial<
    Record<TIMERECORDING_TYPE_OF_WORK_CODES, TIMERECORDING_TYPE_OF_WORK_CODES>
> = {
    [TIMERECORDING_TYPE_OF_WORK_CODES.ROSN]: TIMERECORDING_TYPE_OF_WORK_CODES.ROSR,
    [TIMERECORDING_TYPE_OF_WORK_CODES.FCCN]: TIMERECORDING_TYPE_OF_WORK_CODES.FCC,
};

function isJsonTimeRecordingGeneralEntryModel(
    entry: JsonTimeRecordingGeneralEntryModel | JsonTimeRecordingLimitedEntryModel,
): entry is JsonTimeRecordingGeneralEntryModel {
    return (<JsonTimeRecordingGeneralEntryModel>entry).name === 'TimeRecordingGeneralEntryModel';
}

export class ConfirmationTimeRecordingModel extends CompositeModel {
    private eventService: EventService;
    public timeRecordingEntries: ArrayModel<TimeRecordingEntryModel>;
    public otherTimeRecordingEntries: TimesheetEntryInterface[];
    public timesheetEntries: TimesheetEntryInterface[];
    public billablePresetModel: DropdownModel<TextModel>;
    public afterHourCallOut: DisplayDecorator<BooleanModel>;
    public activityCodes: CodeModel[];
    public configService: ConfigInterface;

    private log: LogInterface;
    private timeRecordingService: TimeRecordingService;
    private notificationService: NotificationDataInterface;
    private readonly CODE_TYPE_FOR_ACTIVITIES = 'A';
    private readonly enableStandbyTravel: boolean;
    public readonly isNotificationRemoteWork: boolean;
    public constructor(
        public notification: NotificationModel,
        private timeRecordingBillableCodes: ValueHelperModel[],
        private timeRecordingTaskTypeInfoCodes: ValueHelperModel[],
        private timeRecordingMeansOfTransportationCodes: ValueHelperModel[],
        private timeRecordingDestinationCodes: ValueHelperModel[],
        timesheet: TimesheetModel,
        private phoneFixIndicatorAA = false,
    ) {
        super('ConfirmationTimeRecordingModel');

        const injector = CustomInjector.getInjector();
        this.log = injector.get(LogInstanceToken);
        this.timeRecordingService = injector.get(TimeRecordingService);
        this.notificationService = injector.get(NotificationDataInstanceToken);
        this.eventService = injector.get(EventService);
        this.configService = injector.get(ConfigInstanceToken);

        this.enableStandbyTravel = this.configService.getEnableStandbyTravel();
        const billableEntries = timeRecordingBillableCodes.map((vh: ValueHelperModel) => {
            return new DisplayDecorator<TextModel>(vh.descText, new TextModel('BillableEntry', vh.code));
        });
        this.billablePresetModel = new DropdownModel<TextModel>('BillablePreset', billableEntries);
        this.billablePresetModel.selectByPredicate((billable) => {
            return billable.value === '';
        });

        // filter only the codes that are relevant for activities
        const activityCodes = notification.codeSet.filter((codeModel) => {
            return codeModel.codeType.value === this.CODE_TYPE_FOR_ACTIVITIES;
        });
        const notificationType = this.notification.notificationType;
        const process = this.notification.processIndicator;
        const currentStatus = this.notificationService.getStatus(this.notification).statusCode;
        this.activityCodes = this.timeRecordingService.getRelevantActivityCodes(
            activityCodes,
            notificationType,
            process,
            currentStatus,
        );

        const timeRecordingRawEntries: TimeRecordingEntryModel[] = [];
        this.timeRecordingEntries = <ArrayModel<TimeRecordingEntryModel>>(
            this.set(new ArrayModel<TimeRecordingEntryModel>('WorkSet', timeRecordingRawEntries))
        );
        this.isNotificationRemoteWork = this.notificationService.isNotificationRemoteWork(this.notification);
        this.addInitialEntries(this.timeRecordingEntries, this.isNotificationRemoteWork);
        this.otherTimeRecordingEntries = this.getOtherNotificationsEntries(timesheet);
        this.timesheetEntries = this.getTimesheetEntries(timesheet);
        if (this.configService.getEnabledAfterHoursCallOut()) {
            this.afterHourCallOut = new DisplayDecorator('', new BooleanModel('afterHoursCallOut', false));
        }
    }

    private getOtherNotificationsEntries(timesheet: Readonly<TimesheetModel>): TimesheetEntryInterface[] {
        let otherTRentries: TimesheetEntryInterface[] = [];
        if (this.configService.getAllowNotificationPause()) {
            // get the in-progress and to-be-signed notifications
            const inProgressNotifications = this.notificationService
                .getAllNotificationsInProgress()
                .filter((notif) => !this.notification.identifier.equalsTo(notif.identifier));
            const toBeSignedNotifications = this.notificationService
                .getAllNotificationsToBeSigned()
                .filter((notif) => !this.notification.identifier.equalsTo(notif.identifier));
            const otherNotifications = [...inProgressNotifications, ...toBeSignedNotifications];
            if (otherNotifications.length > 0) {
                let timesheetEntries: TimesheetEntryInterface[] = [];
                const timesheetNotifications = timesheet.notifications.filter(
                    (tsNotification) =>
                        !this.notification.identifier.equalsTo(tsNotification.notificationId) &&
                        !tsNotification.isConfirmationSubmitted,
                );
                // get timesheet entries of the in-progress and to be signed notifications
                timesheetEntries = _.chain(timesheetNotifications)
                    .map((timesheetNotification) => timesheetNotification.timesheetEntries)
                    .flatten()
                    .value();
                // if timesheet entries of any notification is not present (could be because CRM screen is not yet opened)
                // then create and add initial timesheet entries for such notification
                otherNotifications.forEach((notif) => {
                    const existing = timesheetEntries.find((entry) =>
                        (<TimesheetEntryModel>entry).notificationId.equalsTo(notif.identifier),
                    );
                    if (typeof existing === 'undefined') {
                        timesheetEntries.push(...this.addInitialTSEntriesForNotification(notif));
                    }
                });
                otherTRentries = TimesheetModel.sortTimesheetEntries(timesheetEntries);
                // set time model options readonly value to really disable them. This is needed for custom time inputs
                otherTRentries.forEach((entry) => {
                    entry.startTime = assignModelOptions(
                        entry.startTime,
                        {
                            ...entry.startTime.options,
                            readonly: true,
                        },
                        entry,
                    );

                    entry.endTime = assignModelOptions(
                        entry.endTime,
                        {
                            ...entry.endTime.options,
                            readonly: true,
                        },
                        entry,
                    );
                });
            }
        }
        return otherTRentries;
    }

    private getTimesheetEntries(currentTimesheet: Readonly<TimesheetModel>): TimesheetEntryInterface[] {
        let timesheetEntries: TimesheetEntryInterface[] = [];
        timesheetEntries = currentTimesheet.getTimesheetEntries();
        // set time model options readonly value to really disable them. This is needed for custom time inputs
        timesheetEntries.forEach((entry) => {
            entry.startTime = assignModelOptions(
                entry.startTime,
                {
                    ...entry.startTime.options,
                    readonly: true,
                },
                entry,
            );
            entry.endTime = assignModelOptions(
                entry.endTime,
                {
                    ...entry.endTime.options,
                    readonly: true,
                },
                this,
            );
        });
        return timesheetEntries;
    }

    public clearTimeRecording(): void {
        this.timeRecordingEntries.value.length = 0;
    }

    public clearTimeRecordingGeneralEntries(): void {
        _.remove(
            this.timeRecordingEntries.value,
            (timeRecEntry: TimeRecordingEntryModel) => timeRecEntry instanceof TimeRecordingGeneralEntryModel,
        );
    }

    public resetTimeRecording(): void {
        this.eventService.triggerPhoneFixSelected(false);
        this.billablePresetModel.selectByPredicate((billable) => billable.value === '');
        this.clearTimeRecording();
        this.addInitialEntries(this.timeRecordingEntries, this.isNotificationRemoteWork);
    }

    /**
     * Adding Initial entries for Remote work
     *
     * @param entries time recording entries
     */
    private addInitialEntriesForRemoteWork(entries: ArrayModel<TimeRecordingEntryModel>): void {
        const currentStatus = this.notificationService.getStatus(this.notification).statusCode;
        // in case the app had been deleted before, the local storage would be empty, so we need to work with default values
        const now = moment().startOf('minute');

        let remoteStatus = this.notificationService.getNotificationStatusByStatusType(this.notification, currentStatus);
        if (!remoteStatus) {
            remoteStatus = new StatusModel(currentStatus, now.clone(), now.clone());
        }
        const endTime = remoteStatus.startTime.isAfter(now) ? remoteStatus.startTime : now;
        let typeOfWorkCode: TIMERECORDING_TYPE_OF_WORK_CODES | undefined;
        if (currentStatus === NOTIFICATION_STATUS_CODE.ROSR) {
            typeOfWorkCode = this.timeRecordingTaskTypeInfoCodes.some(
                (vh) => vh.code === TIMERECORDING_TYPE_OF_WORK_CODES.ROSN,
            )
                ? undefined
                : TIMERECORDING_TYPE_OF_WORK_CODES.ROSR;
        } else {
            typeOfWorkCode = this.timeRecordingTaskTypeInfoCodes.some(
                (vh) => vh.code === TIMERECORDING_TYPE_OF_WORK_CODES.FCCN,
            )
                ? undefined
                : TIMERECORDING_TYPE_OF_WORK_CODES.FCC;
        }
        const entryRemote = this.createNewGeneralTimeRecordingModel(
            remoteStatus.startTime,
            endTime,
            typeOfWorkCode,
            true,
            undefined,
        );
        entries.addModel(entryRemote);
    }

    public addInitialEntries(entries: ArrayModel<TimeRecordingEntryModel>, isNotificationRemoteWork = false): void {
        if (isNotificationRemoteWork) {
            this.addInitialEntriesForRemoteWork(entries);
        } else {
            let statusTRAV = this.notificationService.getNotificationStatusByStatusType(
                this.notification,
                NOTIFICATION_STATUS_CODE.TRAV,
            );
            let statusOSR = this.notificationService.getNotificationStatusByStatusType(
                this.notification,
                NOTIFICATION_STATUS_CODE.OSR,
            );

            // in case the app had been deleted before, the local storage would be empty, so we need to work with default values
            const now = moment().startOf('minute');
            if (!statusTRAV) {
                statusTRAV = new StatusModel(NOTIFICATION_STATUS_CODE.TRAV, now.clone(), now.clone());
            }
            if (!statusOSR) {
                statusOSR = new StatusModel(NOTIFICATION_STATUS_CODE.OSR, now.clone(), now.clone());
            }

            const durationValueIsUnchanged = true;
            // If standby travel is enabled, default travel entry will be Standby
            const entryTRTO = this.createNewLimitedTimeRecordingModel(
                statusTRAV.startTime,
                statusOSR.startTime,
                this.enableStandbyTravel
                    ? TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY
                    : TIMERECORDING_TYPE_OF_WORK_CODES.TRTO,
                durationValueIsUnchanged,
            );

            const osrEndTime = statusOSR.startTime.isAfter(now) ? statusOSR.startTime : now;

            const entryOSR = this.createNewGeneralTimeRecordingModel(
                statusOSR.startTime,
                osrEndTime,
                TIMERECORDING_TYPE_OF_WORK_CODES.OSR,
                false,
                this.activityCodes.length === 1 ? this.activityCodes[0] : undefined,
            );

            const entryTRBK = this.createNewLimitedTimeRecordingModel(
                osrEndTime,
                osrEndTime,
                TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
                durationValueIsUnchanged,
            );

            entries.addModel(entryTRTO);
            entries.addModel(entryOSR);
            entries.addModel(entryTRBK);
        }
    }

    private addInitialTSEntriesForRemoteWork(notification: NotificationModel): TimesheetEntryModel[] {
        const entries: TimesheetEntryModel[] = [];
        const currentStatus = this.notificationService.getStatus(notification);

        entries.push(
            new TimesheetEntryModel(
                notification.identifier,
                currentStatus.startTime,
                currentStatus.endTime,
                currentStatus.statusCode === NOTIFICATION_STATUS_CODE.ROSR
                    ? TIMERECORDING_TYPE_OF_WORK_CODES.ROSR
                    : TIMERECORDING_TYPE_OF_WORK_CODES.FCC,
                '',
                undefined,
                undefined,
                '',
                false,
            ),
        );
        return entries;
    }

    private addInitialTSEntriesForNotification(notification: NotificationModel): TimesheetEntryModel[] {
        const entries: TimesheetEntryModel[] = [];
        if (this.notificationService.isNotificationRemoteWork(notification)) {
            return this.addInitialTSEntriesForRemoteWork(notification);
        } else {
            const statusTRAV = this.notificationService.getNotificationStatusByStatusType(
                notification,
                NOTIFICATION_STATUS_CODE.TRAV,
            );
            const statusOSR = this.notificationService.getNotificationStatusByStatusType(
                notification,
                NOTIFICATION_STATUS_CODE.OSR,
            );

            if (statusTRAV) {
                entries.push(
                    new TimesheetEntryModel(
                        notification.identifier,
                        statusTRAV.startTime,
                        statusTRAV.startTime,
                        TIMERECORDING_TYPE_OF_WORK_CODES.TRTO,
                        '',
                        undefined,
                        undefined,
                        '',
                        false,
                    ),
                );
            }

            if (statusOSR) {
                entries.push(
                    new TimesheetEntryModel(
                        notification.identifier,
                        statusOSR.startTime,
                        statusOSR.startTime,
                        TIMERECORDING_TYPE_OF_WORK_CODES.OSR,
                        '',
                        undefined,
                        undefined,
                        '',
                        false,
                    ),
                );
            }
        }
        return entries;
    }

    public createNewLimitedTimeRecordingModel(
        startTime: moment.Moment,
        endTime: moment.Moment,
        type: TIMERECORDING_TYPE_OF_WORK_CODES | undefined,
        durationValueIsUnchanged: boolean,
    ): TimeRecordingLimitedEntryModel {
        // If standby travel is enabled, the travel type depends on whether a notification order type is billable or not
        return new TimeRecordingLimitedEntryModel(
            startTime.clone(),
            endTime.clone(),
            type,
            this.enableStandbyTravel && this.configService.getIsTravelBillable(this.notification.orderType)
                ? ConfigConstants.BILLABLE_CODE.BILLABLE
                : ConfigConstants.BILLABLE_CODE.AUTO,
            durationValueIsUnchanged,
            TIMERECORDING_MEANS_OF_TRANSPORT_CODES.CAR,
            this.timeRecordingBillableCodes,
            this.timeRecordingTaskTypeInfoCodes,
            this.timeRecordingMeansOfTransportationCodes,
        );
    }

    public createNewGeneralTimeRecordingModel(
        startTime: moment.Moment,
        endTime: moment.Moment,
        typeOfWorkCode: TIMERECORDING_TYPE_OF_WORK_CODES | undefined,
        isDurationUnchanged: boolean,
        codeModel?: CodeModel,
    ): TimeRecordingGeneralEntryModel {
        return new TimeRecordingGeneralEntryModel(
            startTime.clone(),
            endTime.clone(),
            typeOfWorkCode,
            ConfigConstants.BILLABLE_CODE.AUTO,
            codeModel ? codeModel.codeName.value : '',
            this.activityCodes,
            this.timeRecordingBillableCodes,
            this.timeRecordingTaskTypeInfoCodes,
            this.phoneFixIndicatorAA,
            isDurationUnchanged,
        );
    }

    public addPhoneFixEntry(workCode: TIMERECORDING_TYPE_OF_WORK_CODES | undefined): void {
        const currentNormalizedTime = moment().startOf('minute');
        let statusOSR = this.notificationService.getNotificationStatusByStatusType(
            this.notification,
            NOTIFICATION_STATUS_CODE.OSR,
        );

        if (!statusOSR) {
            // in case the app had been deleted before, the local storage would be empty, so we need to work with default values
            statusOSR = new StatusModel(
                NOTIFICATION_STATUS_CODE.OSR,
                currentNormalizedTime.clone(),
                currentNormalizedTime.clone(),
            );
        }
        const endTime = statusOSR.startTime.clone();

        const entryPhoneFix = this.createNewGeneralTimeRecordingModel(
            statusOSR.startTime,
            endTime,
            workCode,
            false,
            undefined,
        );
        entryPhoneFix.disableBillableDropdown();
        this.timeRecordingEntries.addModel(entryPhoneFix);
        this.eventService.triggerPhoneFixSelected(true);
        if (this.enableStandbyTravel) {
            // if standby travel is enabled, the phoneFix entry position is decided based on the number of travel-to entries.
            const travelToEntries = this.timeRecordingService.getTravelToEntries(this.timeRecordingEntries.value);
            this.timeRecordingEntries.addModelAtIndex(entryPhoneFix, travelToEntries.length);
        }
    }

    public removeTimeRecordingEntry(index: number): void {
        this.timeRecordingEntries.removeModel(index);
    }

    public getMaximumActivityCount(): number {
        return this.activityCodes.length;
    }

    public getOutModel(): ODataConfirmationTimeRecordingOutModel {
        let outModel: ODataConfirmationTimeRecordingOutModel = {};

        let phoneFixEntry: TimeRecordingEntryModel | undefined;
        // If Confirmation is Remote support or On-site repair
        const firstTimeRecEntry = this.timeRecordingService.getFirstTimeRecordingEntry(this.timeRecordingEntries.value);
        // If notification is not Remote work then the first entry must be Travel to entry else its "phone fix"
        if (this.isNotificationRemoteWork || firstTimeRecEntry?.isTravelToEntry()) {
            const crmCodes = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet'));

            this.timeRecordingEntries.value.forEach((timeRecEntry: TimeRecordingEntryModel) => {
                if (timeRecEntry instanceof TimeRecordingGeneralEntryModel && timeRecEntry.isActivityRequired()) {
                    crmCodes.addModel(timeRecEntry.getActivityCodesModel());
                }
            });

            outModel = super.getOutModel();
            this.replaceNonBillableRemoteWorkCodes(outModel);
        } else {
            // it is the "Phone Fix Case", but let's check that first.
            if (
                firstTimeRecEntry &&
                isTimeRecordingGeneralEntryModel(firstTimeRecEntry) &&
                firstTimeRecEntry.isPhoneFix()
            ) {
                phoneFixEntry = firstTimeRecEntry;
            } else {
                this.log.error(
                    'ConfirmationTimeRecordingModel : getOutModel(): The only time recording entry in the list was illegally not a phone fix/ remote support row. Entry: ' +
                        JSON.stringify(firstTimeRecEntry),
                );
            }
        }

        // Handle Phone Fix case
        if (phoneFixEntry) {
            // The  "Phone Fix Case" does not add values to CrmCodesSet. It rather sets four properties on the CrmHeaderSet itself.
            const phoneFixStartDate = new DateModel('PhoneFixStartDate', phoneFixEntry.startDateTime);
            _.merge(outModel, phoneFixStartDate.getOutModel());

            const phoneFixStartTime = new TimeModel('PhoneFixStartTime', phoneFixEntry.startDateTime);
            _.merge(outModel, phoneFixStartTime.getOutModel());

            const phoneFixEndDate = new DateModel('PhoneFixEndDate', phoneFixEntry.endDateTime);
            _.merge(outModel, phoneFixEndDate.getOutModel());

            const phoneFixEndTime = new TimeModel('PhoneFixEndTime', phoneFixEntry.endDateTime);
            _.merge(outModel, phoneFixEndTime.getOutModel());
        }
        return outModel;
    }

    /**
     * The backend does not know the non-billable remote work codes, so they are sent as their billable
     * counterpart. This works on the generated payload instead of on the entries: getOutModel() also runs while
     * the confirmation is being saved, and re-selecting the dropdown there would overwrite what the user picked.
     *
     * @param outModel the payload generated for this model, modified in place
     */
    private replaceNonBillableRemoteWorkCodes(outModel: ODataConfirmationTimeRecordingOutModel): void {
        const workEntries = outModel[this.timeRecordingEntries.name];
        if (!Array.isArray(workEntries)) {
            return;
        }

        workEntries.forEach((workEntry) => {
            if (typeof workEntry !== 'object' || workEntry === null || Array.isArray(workEntry)) {
                return;
            }
            const substitute =
                NON_BILLABLE_REMOTE_WORK_CODE_SUBSTITUTES[<TIMERECORDING_TYPE_OF_WORK_CODES>workEntry[WORK_CODE_FIELD]];
            if (substitute) {
                workEntry[WORK_CODE_FIELD] = substitute;
            }
        });
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns the JSON representation of the model
     */
    public toJSON(): unknown {
        return {
            billablePresetValue: this.billablePresetModel.selectedEntry,
            timeRecordingEntries: this.timeRecordingEntries.value,
            afterHourCallOutFlag: this.afterHourCallOut?.model.value ?? false,
        };
    }
}

export class ConfirmationTimeRecordingModelFromJson extends ConfirmationTimeRecordingModel {
    public constructor(
        data: JsonConfirmationTimeRecordingModel,
        notification: NotificationModel,
        timeRecordingBillableCodes: ValueHelperModel[],
        timeRecordingTaskTypeInfoCodes: ValueHelperModel[],
        timeRecordingMeansOfTransportationCodes: ValueHelperModel[],
        timeRecordingDestinationCodes: ValueHelperModel[],
        timesheet: TimesheetModel,
        phoneFixIndicatorAA: boolean,
    ) {
        super(
            notification,
            timeRecordingBillableCodes,
            timeRecordingTaskTypeInfoCodes,
            timeRecordingMeansOfTransportationCodes,
            timeRecordingDestinationCodes,
            timesheet,
            phoneFixIndicatorAA,
        );

        if (this.afterHourCallOut) {
            this.afterHourCallOut.model.value = data.afterHourCallOutFlag;
        }

        if (data.billablePresetValue) {
            this.billablePresetModel.selectByDisplayName(data.billablePresetValue.displayName);
        }
        // re-create timeRecordingEntries
        this.timeRecordingEntries.value.length = 0;
        data.timeRecordingEntries.forEach(
            (entry: JsonTimeRecordingGeneralEntryModel | JsonTimeRecordingLimitedEntryModel) => {
                if (isJsonTimeRecordingGeneralEntryModel(entry)) {
                    this.timeRecordingEntries.addModel(
                        new TimeRecordingGeneralEntryModelFromJson(
                            entry,
                            timeRecordingBillableCodes,
                            timeRecordingTaskTypeInfoCodes,
                            phoneFixIndicatorAA,
                        ),
                    );
                } else {
                    this.timeRecordingEntries.addModel(
                        new TimeRecordingLimitedEntryModelFromJson(
                            entry,
                            timeRecordingBillableCodes,
                            timeRecordingTaskTypeInfoCodes,
                            timeRecordingMeansOfTransportationCodes,
                        ),
                    );
                }
            },
        );
        // Migration logic starts:
        // If the ActivityCode dropdown was saved previously, it may contain all possible values.
        // Therefore, we need to filter the activity codes based on the process indicator, notification status and notification type.
        // This logic is temporary and can be removed after a couple of versions
        // once legacy data is no longer in use.
        this.timeRecordingEntries.value.forEach((entry) => {
            if (entry instanceof TimeRecordingGeneralEntryModel) {
                entry.updateActivityCode(this.activityCodes);
            }
        });
        // Migration Logic ends
    }
}

export interface JsonConfirmationTimeRecordingModel {
    billablePresetValue: DisplayDecorator<TextModel>;
    timeRecordingEntries: Array<JsonTimeRecordingGeneralEntryModel | JsonTimeRecordingLimitedEntryModel>;
    afterHourCallOutFlag: boolean;
}

export type ODataConfirmationTimeRecordingOutModel = ODataObject;
